// Pure config for the image uploaders: one policy, parameterized by the crop
// aspect, the stored size limit and the fixed output. Kept as data so it is
// unit-testable without Uppy.
export const IMAGE_FILE_TYPES = ['.jpg', '.jpeg', '.png']
export const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png']

// A picture straight off a phone or camera is accepted as it is: it is cut
// down to the fixed output before upload, and only that output has to fit
// the stored limit (maxFileSize).
export const MAX_SOURCE_SIZE = 20 * 1024 * 1024

// `type: 'keep'` keeps the source's type (a PNG logo keeps its
// transparency); a fixed type is always written.
export const JPEG = 'image/jpeg'
export const PNG = 'image/png'
export const KEEP = 'keep'

// Node thumbnails: the platform's one shape, 2:1 at 1024x512 -- what the
// Unity editor captures on publish and what every course card (.course-image)
// and the apps draw. Always JPEG.
export const THUMBNAIL_OUTPUT = { width: 1024, height: 512, type: JPEG, quality: 0.85 }

// The trade show and self-register hero pictures: 16:9, drawn full width in
// a col-md-8/col-xl-6 (trade show) or col-lg-8 (self-register) column, at
// most ~860 CSS px; 1280x720 is sharp there on most screens.
export const HERO_OUTPUT = { width: 1280, height: 720, type: KEEP, quality: 0.85 }

// The organization's logo: 13:3, drawn 28px high in the header (.logo), so
// ~121x28 CSS px; 520x120 covers a 4x screen.
export const ORGANIZATION_LOGO_OUTPUT = { width: 520, height: 120, type: KEEP, quality: 0.9 }

// Uppy's image editor toolbar, locked: only zoom stays. Every other default
// action breaks the fixed aspect -- the square/16:9/9:16 buttons call
// cropper.setAspectRatio, and Revert calls
// setAspectRatio(cropperOptions.initialAspectRatio), which Uppy defaults to
// 0 (freeform) -- or changes the picture in ways the card never shows
// (rotate, flip).
export const LOCKED_ACTIONS = {
  revert: false,
  rotate: false,
  granularRotate: false,
  flip: false,
  zoomIn: true,
  zoomOut: true,
  cropSquare: false,
  cropWidescreen: false,
  cropWidescreenVertical: false,
}

const kb = bytes => Math.round(bytes / 1024)

export const buildImageUploadConfig = ({ aspect, maxFileSize, output, preview }) => {
  const [w, h] = aspect
  const ratio = w / h

  return {
    // Checked when a file is added: type, count and the generous source limit.
    restrictions: {
      maxNumberOfFiles: 1,
      maxFileSize: MAX_SOURCE_SIZE,
      allowedFileTypes: IMAGE_FILE_TYPES,
    },
    // Checked on the final output, just before upload (image-output.js).
    maxFileSize,
    output,
    imageEditor: {
      actions: LOCKED_ACTIONS,
      quality: output.quality,
      // Cropper.js options (@uppy/image-editor 3.4.2 hands them to
      // `new Cropper` as they are, and croppedCanvasOptions to
      // getCroppedCanvas on Save):
      // - aspectRatio + initialAspectRatio: the crop is always w:h, also
      //   after anything that resets it to the initial one.
      // - viewMode 1: the crop box stays inside the picture.
      // - autoCropArea 1: it starts as large as the picture allows.
      // - dragMode 'move': dragging moves the picture under the box, never
      //   draws a new box; a double click doesn't switch that.
      // - preview: the card-shaped live preview beside the stage.
      // - croppedCanvasOptions: Save already writes the output size.
      cropperOptions: {
        aspectRatio: ratio,
        initialAspectRatio: ratio,
        viewMode: 1,
        dragMode: 'move',
        toggleDragModeOnDblclick: false,
        autoCropArea: 1,
        responsive: true,
        background: false,
        ...(preview ? { preview } : {}),
        croppedCanvasOptions: {
          width: output.width,
          height: output.height,
          imageSmoothingEnabled: true,
          imageSmoothingQuality: 'high',
          ...(output.type === JPEG ? { fillColor: '#fff' } : {}),
        },
      },
    },
    note: `JPEG/PNG · saved at ${output.width}×${output.height} · max ${kb(maxFileSize)} KB`,
  }
}
